import { useCallback, useEffect, useMemo, useState, type ComponentProps } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Eye, Play, Sparkles } from "lucide-react";
import { api, errorMessage, post } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/shell/Toast";
import { CourseWalkthrough } from "@/components/courses/CourseWalkthrough";
import type { WalkStep } from "@/components/courses/builderWalkthrough";
import CoursePreviewDialog from "@/components/courses/CoursePreviewDialog";
import StudentItemView from "@/components/courses/StudentItemView";
import { Mascot } from "@/pages/student/Mascot";
import DemoDiscussion from "@/components/demo/DemoDiscussion";
import ButlerChat, { type ButlerSpot } from "@/components/demo/ButlerChat";
import TryItDialog from "@/components/demo/TryItDialog";
import DemoItemResults, { type ResultsKind } from "@/components/demo/DemoItemResults";
import { DEMO_COURSE_ID } from "@/constants/publicSite";
import { splitUnitItems } from "@/lib/unitPlacement";
import "./DemoCourse.css";

/*
 * Guided demo of a real course (/features/demo). Read-only: this page only
 * reads the course and its questions. The one write is "Try it yourself",
 * which makes the visitor their own starter course.
 */

type DemoActivity = {
  id: string;
  type: string;
  label?: string;
  title?: string;
  description?: string;
  body?: { type: string; html?: string }[] | null;
  content?: ({ terms?: { term: string; definition: string }[] } & Record<string, unknown>) | null;
  metadata?: ({ assignmentPoints?: number } & Record<string, unknown>) | null;
  estimatedMinutes?: number | null;
  timeLimitMinutes?: number | null;
  dueAt?: string | null;
  totalPoints?: number | null;
  maxAttempts?: number | null;
  questionCount?: number | null;
  published?: boolean;
  complete?: boolean;
};

type DemoContainer = {
  id: string;
  type: string;
  label?: string;
  typeLabel?: string;
  title?: string;
  objectives?: string[] | null;
  activities: DemoActivity[];
  children: DemoContainer[];
};

type DemoStructure = {
  course: { id: string; title: string; description?: string | null };
  containers: DemoContainer[];
  activities: DemoActivity[];
};

type DemoQuestion = {
  id: string;
  prompt: string;
  type: string;
  points?: number | null;
  options?: { id: number; label: string }[] | null;
  correct?: { optionId?: number; optionIds?: number[] } | null;
  scoring?: { answerMode?: string } | null;
};

type PreviewStructure = ComponentProps<typeof CoursePreviewDialog>["structure"];

const TYPE_NAMES: Record<string, string> = {
  reading: "Reading",
  audio: "Audio",
  video: "Video",
  slides: "Slides",
  key_terms: "Key terms",
  discussion: "Discussion",
  homework: "Homework",
  assessment: "Check",
  exam: "Exam",
  project: "Project",
  lab: "Lab",
};

const TYPE_TONE: Record<string, string> = {
  reading: "content",
  audio: "content",
  video: "content",
  slides: "content",
  key_terms: "content",
  discussion: "talk",
  homework: "work",
  project: "work",
  lab: "work",
  assessment: "check",
  exam: "exam",
};

const STEPS: WalkStep[] = [
  {
    id: "welcome",
    target: "demo-header",
    pose: "waving",
    text: "Welcome! This is Algebra 1, a real course built in AcuityLearn. I'll show you the highlights in about two minutes. Nothing you do here is saved.",
  },
  {
    id: "outline",
    target: "demo-unit",
    pose: "pointing",
    text: "Here's Unit 1 at a glance: an overview, a readiness check, four lessons and a 20-question exam. Unit 2 below follows the same shape.",
  },
  {
    id: "lesson",
    target: "demo-lesson",
    pose: "book",
    text: "Every lesson follows the same order: read, listen, learn the key terms, discuss, practice, homework, then a check.",
  },
  {
    id: "terms",
    target: "demo-detail",
    pose: "laptop",
    text: "Key terms are a list of words with definitions. Students get them as flashcards automatically.",
  },
  {
    id: "check",
    target: "demo-detail",
    pose: "thinking",
    text: "This is the lesson's check. Eight questions, 40 points, three tries, and every question grades itself. Open Results to see who turned it in and which question the class missed most.",
  },
  {
    id: "discussion",
    target: "demo-detail",
    pose: "reading",
    text: "Here's a discussion with sample students. Type a grade and a comment for a reply, click a student's picture to message them, or click their name to see their profile and parent contacts. Nothing is saved or sent.",
  },
  {
    id: "student",
    target: "demo-switch",
    pose: "pointing",
    text: "Any item can be seen the way a student sees it. Click Student at the top of this panel. Preview course walks through the whole course.",
  },
];

function plainText(body: DemoActivity["body"], words = 70): string {
  const html = (body || []).map((block) => block.html || "").join(" ");
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  const parts = text.split(" ");
  return parts.length > words ? `${parts.slice(0, words).join(" ")}…` : text;
}

function deepActivities(node: DemoContainer): DemoActivity[] {
  return [...node.activities, ...node.children.flatMap(deepActivities)];
}

/** Hide units that are only empty template placeholders. */
function hasFinishedWork(node: DemoContainer): boolean {
  return deepActivities(node).some((item) => item.complete);
}

function typeName(item: DemoActivity) {
  return TYPE_NAMES[item.type] || item.label || item.type;
}

function points(item: DemoActivity): number {
  return item.totalPoints || item.metadata?.assignmentPoints || 0;
}

/** Which Results view an item gets in the demo, or null when it has none (readings, videos…). */
function resultsKindOf(item: DemoActivity, questions?: DemoQuestion[] | null): ResultsKind | null {
  if (item.type === "discussion") return "discussion";
  if ((item.questionCount || 0) > 0 && ["homework", "assessment", "exam"].includes(item.type)) {
    // Only questions with answer choices grade themselves; an item that is just a file upload is graded by hand.
    const autoGraded = (q: DemoQuestion) => (q.options?.length || 0) > 0 && (q.correct?.optionId != null || (q.correct?.optionIds?.length || 0) > 0);
    if (questions && !questions.some(autoGraded)) return "upload";
    return "quiz";
  }
  if (["homework", "project", "lab"].includes(item.type)) return "upload";
  return null;
}

function ItemRow({
  item,
  selected,
  onSelect,
  tour,
}: {
  item: DemoActivity;
  selected: boolean;
  onSelect: (item: DemoActivity) => void;
  tour?: string;
}) {
  const pts = points(item);
  return (
    <li>
      <button
        type="button"
        className={`al-demo-item${selected ? " is-on" : ""}`}
        aria-pressed={selected}
        onClick={() => onSelect(item)}
        data-tour={tour}
      >
        <span className={`al-demo-kind al-demo-kind--${TYPE_TONE[item.type] || "content"}`}>{typeName(item)}</span>
        <span className="al-demo-item-title">{item.title?.trim() || typeName(item)}</span>
        <span className="al-demo-item-meta">
          {pts ? `${pts} pts` : item.estimatedMinutes ? `${item.estimatedMinutes} min` : ""}
        </span>
      </button>
    </li>
  );
}

export default function DemoCourse() {
  const navigate = useNavigate();
  const toast = useToast();
  const { capabilities } = useAuth();
  const [structure, setStructure] = useState<DemoStructure | null>(null);
  const [loadError, setLoadError] = useState("");
  const [openLessons, setOpenLessons] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<DemoActivity | null>(null);
  const [questions, setQuestions] = useState<DemoQuestion[] | null>(null);
  const [questionsError, setQuestionsError] = useState("");
  const [tourOn, setTourOn] = useState(true);
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tryOpen, setTryOpen] = useState(false);
  const [teacherTab, setTeacherTab] = useState<"details" | "results">("details");
  const [spot, setSpot] = useState<ButlerSpot | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [view, setView] = useState<"teacher" | "student">("teacher");

  useEffect(() => {
    document.title = "AcuityLearn — Demo";
    let alive = true;
    api<{ success: boolean; data: DemoStructure }>(`/api/courses/${DEMO_COURSE_ID}/structure`)
      .then((envelope) => {
        if (!alive) return;
        setStructure(envelope.data);
      })
      .catch((err) => {
        if (alive) setLoadError(errorMessage(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  const units = useMemo(
    () => (structure ? structure.containers.filter(hasFinishedWork) : []),
    [structure],
  );
  const firstLesson = units[0]?.children[0] || null;
  const lessonTerms = firstLesson?.activities.find((item) => item.type === "key_terms") || null;
  const lessonReading = firstLesson?.activities.find((item) => item.type === "reading") || null;
  const lessonDiscussion = firstLesson?.activities.find((item) => item.type === "discussion") || null;
  const lessonCheck =
    firstLesson?.activities.find((item) => item.type === "assessment") ||
    firstLesson?.activities.find((item) => (item.questionCount || 0) > 0) ||
    null;

  const select = useCallback((item: DemoActivity | null) => {
    setSelected(item);
    setTeacherTab("details");
    setQuestions(null);
    setQuestionsError("");
    if (!item || !(item.questionCount || 0)) return;
    api<{ success: boolean; data?: DemoQuestion[] }>(`/api/activities/${item.id}/questions`)
      .then((envelope) => setQuestions(envelope.data || []))
      .catch((err) => setQuestionsError(errorMessage(err)));
  }, []);

  // Each tour stop sets up what Butler is talking about.
  useEffect(() => {
    if (!tourOn || !structure) return;
    const id = STEPS[step]?.id;
    if (id === "lesson" && firstLesson) setOpenLessons((open) => ({ ...open, [firstLesson.id]: true }));
    if (id === "terms" && lessonTerms && selected?.id !== lessonTerms.id) select(lessonTerms);
    if (id === "check" && lessonCheck && selected?.id !== lessonCheck.id) select(lessonCheck);
    if (id === "discussion" && lessonDiscussion && selected?.id !== lessonDiscussion.id) select(lessonDiscussion);
    if (id === "student" && lessonReading) {
      if (selected?.id !== lessonReading.id) select(lessonReading);
      setView("teacher");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, tourOn, structure]);

  // Butler's chat answered: set up the page and highlight the spot he named.
  useEffect(() => {
    if (!spot) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.querySelector(".al-demo-spot")?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    const clear = window.setTimeout(() => setSpot(null), 8000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(clear);
    };
  }, [spot]);

  const showSpot = (next: ButlerSpot) => {
    // Everything Butler points at, except Results, lives on the Details tab.
    if (next !== "results") setTeacherTab("details");
    const openFirst = () => {
      if (firstLesson) setOpenLessons((open) => ({ ...open, [firstLesson.id]: true }));
    };
    if ((next === "grade" || next === "dm" || next === "profile") && lessonDiscussion) {
      openFirst();
      setView("teacher");
      if (selected?.id !== lessonDiscussion.id) select(lessonDiscussion);
    }
    if (next === "points" && lessonCheck && selected?.id !== lessonCheck.id) select(lessonCheck);
    if (next === "results" && lessonCheck) {
      openFirst();
      if (selected?.id !== lessonCheck.id) select(lessonCheck);
      setView("teacher");
      window.setTimeout(() => setTeacherTab("results"), 0);
    }
    if (next === "terms" && lessonTerms) {
      openFirst();
      if (selected?.id !== lessonTerms.id) select(lessonTerms);
    }
    if (next === "preview") {
      openFirst();
      if (!selected && lessonReading) select(lessonReading);
    }
    setSpot(null);
    window.setTimeout(() => setSpot(next), 60);
  };

  const previewStructure = useMemo<PreviewStructure | null>(() => {
    if (!structure) return null;
    return { course: structure.course, containers: units, activities: structure.activities } as unknown as PreviewStructure;
  }, [structure, units]);

  const openPreview = () => setPreviewOpen(true);

  /** Teacher / Student switch. Choosing Student during the last tour stop ends the tour. */
  const chooseView = (next: "teacher" | "student") => {
    setView(next);
    if (next === "student" && tourOn && STEPS[step]?.id === "student") {
      setTourOn(false);
      setFinished(true);
    }
  };

  const restartTour = () => {
    setFinished(false);
    setStep(0);
    select(null);
    setTourOn(true);
  };

  /** Opens the template-or-blank chooser; the chooser makes the course. */
  const tryItYourself = () => {
    if (!capabilities?.createCourses) {
      toast("You can explore courses here. To build your own, ask your admin to let you create courses.");
      navigate("/courses");
      return;
    }
    setTryOpen(true);
  };

  const courseCreated = (id: string, templateFailed: boolean) => {
    setCreating(true);
    toast(
      templateFailed
        ? "Your course is ready. The template didn't load, so you can add it from the builder."
        : "Your course is ready. It's under My courses.",
    );
    navigate(`/courses/${id}/build`);
  };

  if (loadError) {
    return (
      <div className="al-demo al-demo--state">
        <Mascot pose="confused" size={120} />
        <h1>The demo course didn't load</h1>
        <p>{loadError}</p>
        <Link to="/features" className="al-demo-btn al-demo-btn--ghost">
          Back to features
        </Link>
      </div>
    );
  }

  if (!structure) {
    return (
      <div className="al-demo al-demo--state" aria-busy="true">
        <Mascot pose="reading" size={120} />
        <p>Loading Algebra 1…</p>
      </div>
    );
  }

  const terms = selected?.content?.terms || [];
  const excerpt = selected ? plainText(selected.body) : "";

  return (
    <div className={`al-demo${chatOpen && !tourOn && !finished && !previewOpen ? " al-demo--chat" : ""}`}>
      <div className="al-demo-banner">
        <span className="al-demo-badge">Demo</span>
        <span>You're looking at a real course. Nothing you do here is saved.</span>
        <div className="al-demo-banner-actions">
          {!tourOn ? (
            <button type="button" className="al-demo-link" onClick={restartTour}>
              <Play size={14} aria-hidden="true" />
              Restart tour
            </button>
          ) : null}
          <Link to="/features" className="al-demo-link">
            <ArrowLeft size={14} aria-hidden="true" />
            Back to features
          </Link>
        </div>
      </div>

      <header className="al-demo-head" data-tour="demo-header">
        <p className="al-demo-crumb">Demo course</p>
        <h1>{structure.course.title}</h1>
        {structure.course.description ? <p className="al-demo-desc">{structure.course.description}</p> : null}
        <div className="al-demo-head-actions">
          <button type="button" className="al-demo-btn al-demo-btn--ghost" onClick={openPreview}>
            <Eye size={15} aria-hidden="true" />
            Preview course
          </button>
          <button
            type="button"
            className={`al-demo-btn${spot === "try" ? " al-demo-spot" : ""}`}
            onClick={tryItYourself}
            disabled={creating}
          >
            <Sparkles size={15} aria-hidden="true" />
            {creating ? "Making your course…" : "Try it yourself"}
          </button>
        </div>
      </header>

      <div className={`al-demo-body${teacherTab === "results" && view === "teacher" && selected && resultsKindOf(selected) ? " is-results" : ""}`}>
        <section className="al-demo-outline" aria-label="Course outline" data-tour="demo-outline">
          <h2 className="al-demo-section-title">Outline</h2>
          {units.map((unit, unitIndex) => (
            <div key={unit.id} className="al-demo-unit" data-tour={unitIndex === 0 ? "demo-unit" : undefined}>
              <div className="al-demo-unit-head">
                <span>
                  {unit.typeLabel || "Unit"} {unitIndex + 1} · {unit.title}
                </span>
                <small>{unit.children.length} lessons</small>
              </div>
              {splitUnitItems(unit.activities).before.length ? (
                <ul className="al-demo-items al-demo-items--unit">
                  {splitUnitItems(unit.activities).before.map((item) => (
                    <ItemRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={select} />
                  ))}
                </ul>
              ) : null}
              {unit.children.map((lesson, lessonIndex) => {
                const open = Boolean(openLessons[lesson.id]);
                const isFirst = lesson.id === firstLesson?.id;
                return (
                  <div key={lesson.id} className={`al-demo-lesson${open ? " is-open" : ""}`} data-tour={isFirst ? "demo-lesson" : undefined}>
                    <button
                      type="button"
                      className="al-demo-lesson-head"
                      aria-expanded={open}
                      onClick={() => setOpenLessons((all) => ({ ...all, [lesson.id]: !open }))}
                    >
                      {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                      <span>
                        Lesson {unitIndex + 1}.{lessonIndex + 1} · {lesson.title}
                      </span>
                      <small>{lesson.activities.length} items</small>
                    </button>
                    {open ? (
                      <>
                        {lesson.objectives?.length ? (
                          <div className="al-demo-goals">
                            <p>By the end of this lesson, students can:</p>
                            <ul>
                              {lesson.objectives.map((goal) => (
                                <li key={goal}>{goal}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        <ul className="al-demo-items">
                          {lesson.activities.map((item) => (
                            <ItemRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={select} />
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                );
              })}
              {splitUnitItems(unit.activities).after.length ? (
                <ul className="al-demo-items al-demo-items--unit">
                  {splitUnitItems(unit.activities).after.map((item) => (
                    <ItemRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={select} />
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </section>

        <aside className="al-demo-detail" aria-label="Item details" aria-live="polite" data-tour="demo-detail">
          {!selected ? (
            <div className="al-demo-empty">
              <Mascot pose="pointing" size={88} />
              <p>Click any item in the outline to see what's inside it.</p>
            </div>
          ) : (
            <>
              <div className="al-demo-detail-head">
                <div>
                  <span className={`al-demo-kind al-demo-kind--${TYPE_TONE[selected.type] || "content"}`}>
                    {typeName(selected)}
                  </span>
                  <h2>{selected.title?.trim() || typeName(selected)}</h2>
                </div>
                <div
                  className={`al-demo-switch${spot === "preview" ? " al-demo-spot" : ""}`}
                  role="group"
                  aria-label="See this item as"
                  data-tour="demo-switch"
                >
                  <button type="button" aria-pressed={view === "teacher"} onClick={() => chooseView("teacher")}>
                    Teacher
                  </button>
                  <button type="button" aria-pressed={view === "student"} onClick={() => chooseView("student")}>
                    <Eye size={13} aria-hidden="true" />
                    Student
                  </button>
                </div>
              </div>

              {view === "student" ? (
                <div className="al-demo-studentview">
                  <p className="al-demo-studentview-note">What a student sees · nothing here is saved</p>
                  <StudentItemView key={selected.id} item={selected} />
                </div>
              ) : (
              <>
              {resultsKindOf(selected) ? (
                <div
                  className={`al-demo-tabs${spot === "results" ? " al-demo-spot" : ""}`}
                  role="tablist"
                  aria-label="Teacher view"
                  data-tour="demo-results"
                >
                  <button type="button" role="tab" aria-selected={teacherTab === "details"} onClick={() => setTeacherTab("details")}>
                    Details
                  </button>
                  <button type="button" role="tab" aria-selected={teacherTab === "results"} onClick={() => setTeacherTab("results")}>
                    Results
                  </button>
                </div>
              ) : null}
              {teacherTab === "results" && resultsKindOf(selected) ? (
                resultsKindOf(selected, questions) === "quiz" && !questions ? (
                  <p className="al-demo-muted">{questionsError || "Loading results…"}</p>
                ) : (
                  <DemoItemResults
                    key={selected.id}
                    itemId={selected.id}
                    title={selected.title?.trim() || typeName(selected)}
                    kind={resultsKindOf(selected, questions) as ResultsKind}
                    points={points(selected)}
                    questions={questions || []}
                    course={structure.course.title.replace(/\s*\(.*?\)\s*$/, "").trim() || "this class"}
                  />
                )
              ) : (
              <>
              <div className={`al-demo-facts${spot === "points" ? " al-demo-spot" : ""}`}>
                {selected.estimatedMinutes ? <span>{selected.estimatedMinutes} min</span> : null}
                {points(selected) ? <span>{points(selected)} points</span> : null}
                {selected.questionCount ? <span>{selected.questionCount} questions</span> : null}
                {selected.maxAttempts ? <span>{selected.maxAttempts} attempts</span> : null}
              </div>

              {terms.length ? (
                <ol className={`al-demo-terms${spot === "terms" ? " al-demo-spot" : ""}`}>
                  {terms.map((row) => (
                    <li key={row.term}>
                      <strong>{row.term}</strong>
                      <span>{row.definition}</span>
                    </li>
                  ))}
                </ol>
              ) : null}

              {selected.questionCount ? (
                questionsError ? (
                  <p className="al-demo-muted">{questionsError}</p>
                ) : !questions ? (
                  <p className="al-demo-muted">Loading questions…</p>
                ) : (
                  <ol className="al-demo-questions">
                    {questions.map((q) => {
                      const right = new Set(
                        q.correct?.optionIds || (q.correct?.optionId != null ? [q.correct.optionId] : []),
                      );
                      const auto = (q.options?.length || 0) > 0 && right.size > 0;
                      return (
                        <li key={q.id}>
                          <div className="al-demo-q-head">
                            <span>{q.prompt}</span>
                            <span className={`al-demo-grade${auto ? "" : " al-demo-grade--manual"}`}>
                              {auto ? "Auto-graded" : "Teacher graded"}
                            </span>
                          </div>
                          {q.options?.length ? (
                            <ul className="al-demo-options">
                              {q.options.map((option) => (
                                <li key={option.id} className={right.has(option.id) ? "is-right" : ""}>
                                  {right.has(option.id) ? (
                                    <Check size={14} aria-label="Correct answer" />
                                  ) : (
                                    <span aria-hidden="true" className="al-demo-dot" />
                                  )}
                                  {option.label}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                )
              ) : null}

              {selected.type === "discussion" ? (
                <DemoDiscussion
                  key={selected.id}
                  prompt={plainText(selected.body, 40).replace(/^Discussion prompt\s*/i, "")}
                  points={points(selected)}
                  spot={spot === "grade" || spot === "dm" || spot === "profile" ? spot : null}
                  course={structure.course.title.replace(/\s*\(.*?\)\s*$/, "").trim() || "this class"}
                />
              ) : null}

              {!terms.length && !selected.questionCount && selected.type !== "discussion" && excerpt ? (
                <p className="al-demo-excerpt">{excerpt}</p>
              ) : null}
              </>
              )}
              </>
              )}
            </>
          )}
        </aside>
      </div>

      {tourOn ? (
        <CourseWalkthrough
          mode="tour"
          steps={STEPS}
          stepIndex={step}
          chapter="Demo"
          outline={[]}
          onJump={setStep}
          onStart={() => setTourOn(true)}
          onDismiss={() => setTourOn(false)}
          onClose={() => setTourOn(false)}
          onBack={() => setStep((n) => Math.max(0, n - 1))}
          onNext={() => {
            if (step >= STEPS.length - 1) {
              setTourOn(false);
              setFinished(true);
            } else {
              setStep((n) => n + 1);
            }
          }}
        />
      ) : null}

      {finished && !previewOpen ? (
        <div className="al-demo-finish" role="dialog" aria-labelledby="al-demo-finish-title">
          <Mascot pose="celebrating" size={104} />
          <div>
            <p className="al-demo-finish-who">Butler</p>
            <p id="al-demo-finish-title" className="al-demo-finish-text">
              That's the tour! Want to build one of your own? Start from our template with the lessons laid out, or start blank.
            </p>
            <div className="al-demo-finish-actions">
              <button type="button" className="al-demo-btn" onClick={tryItYourself} disabled={creating}>
                {creating ? "Making your course…" : "Try it yourself"}
              </button>
              <button type="button" className="al-demo-btn al-demo-btn--ghost" onClick={restartTour}>
                Restart tour
              </button>
              <button type="button" className="al-demo-link" onClick={() => setFinished(false)}>
                Keep exploring
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!tourOn && !finished && !previewOpen ? <ButlerChat onSpot={showSpot} onOpenChange={setChatOpen} /> : null}
      {tryOpen ? <TryItDialog onClose={() => setTryOpen(false)} onCreated={courseCreated} /> : null}

      {previewStructure ? (
        <CoursePreviewDialog open={previewOpen} structure={previewStructure} onClose={() => setPreviewOpen(false)} />
      ) : null}
    </div>
  );
}
